// ===== BEGIN FILE: src/components/NorthIndianChartVedicUMD.tsx =====
import React, { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import '../styles/gradientBorder.css';
import HouseScoresCompactView from './HouseScoresCompactView';
import TransitDateSlider from './TransitDateSlider';

type PlanetName =
  | 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';
export type HousePlacement = { house: number; offsetDeg: number };
export type PlanetsInHouses = Record<PlanetName, HousePlacement>;

declare global {
  interface Window {
    VedicChart?: any;
    NorthIndianChart?: any;
    __UMD?: any;
  }
}

const PLANET_ABBR: Record<PlanetName, string> = {
  Sun:'Su', Moon:'Mo', Mercury:'Me', Venus:'Ve', Mars:'Ma',
  Jupiter:'Ju', Saturn:'Sa', Rahu:'Ra', Ketu:'Ke'
};

function norm360(x:number){ return (x%360+360)%360; }
function signIdx0(lon:number){ return Math.floor(norm360(lon)/30); } // 0..11
function signNum1FromLon(lon:number){ return signIdx0(lon)+1; }      // 1..12

const PX_PER_CM = 96 / 2.54;
const cmToPx = (cm: number) => cm * PX_PER_CM;

/* ========= NEW: planet-name canonicalization & ParÄÅ›ari helpers ========= */
// ===== START INSERT: normalization + ParÄÅ›ari helpers =====
const CANON: Record<string, PlanetName> = {
  // English
  sun:'Sun', moon:'Moon', mercury:'Mercury', venus:'Venus', mars:'Mars',
  jupiter:'Jupiter', saturn:'Saturn', rahu:'Rahu', ketu:'Ketu',
  // Common Sanskrit/colloquial
  surya:'Sun', chandra:'Moon', budha:'Mercury', shukra:'Venus', mangal:'Mars', kuja:'Mars',
  guru:'Jupiter', brihaspati:'Jupiter', shani:'Saturn',
  // Abbreviations
  su:'Sun', mo:'Moon', me:'Mercury', ve:'Venus', ma:'Mars', ju:'Jupiter', sa:'Saturn', ra:'Rahu', ke:'Ketu',
};
function toPlanetNameLoose(s: unknown): PlanetName | null {
  if (typeof s !== 'string') return null;
  const k = s.trim().toLowerCase();
  return CANON[k] ?? null;
}
function clampHouse12(h:number){ return ((h-1)%12+12)%12 + 1; }

type ExtAspect = { from: PlanetName; to?: PlanetName; toHouse?: number; kind?: string; fromHouse?: number };

function parseKindOrdinal(kind?: string): number | null {
  if (!kind) return null;
  const m = kind.match(/(\d+)\s*(st|nd|rd|th)?/i);
  return m ? Math.max(1, Math.min(12, parseInt(m[1], 10))) : null;
}
function nthFrom(h: number, n: number){ return ((h - 1 + (n - 1)) % 12) + 1; }

/** Compute full ParÄÅ›ari set from placements. Optionally include RÄhu/Ketu 5/9. */
function computeParashariAspects(
  placements: PlanetsInHouses,
  includeNodesSpecial: boolean
): ExtAspect[] {
  const out: ExtAspect[] = [];
  const add = (from: PlanetName, toHouse:number, kind:string) => out.push({ from, toHouse: clampHouse12(toHouse), kind });
  (Object.keys(placements) as PlanetName[]).forEach((p)=>{
    const H = clampHouse12(placements[p]?.house ?? 0);
    if (!H) return;
    // everyone 7th
    add(p, H+6, '7th');
    // specials
    if (p==='Mars')    { add(p, H+3, '4th'); add(p, H+7, '8th'); }   // <-- FIXED here
    if (p==='Jupiter'){ add(p, H+4, '5th'); add(p, H+8, '9th');   }
    if (p==='Saturn') { add(p, H+2, '3rd'); add(p, H+9, '10th');  }
    if (includeNodesSpecial && (p==='Rahu' || p==='Ketu')) {
      add(p, H+4, '5th'); add(p, H+8, '9th');
    }
  });
  // dedupe
  const seen = new Set<string>();
  return out.filter(a=>{
    const k = `${a.from}|${a.toHouse}|${a.kind}`;
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
}

/** Normalize incoming aspects: fix planet keys; resolve toHouse from to/kind if needed. */
function normalizeIncomingAspects(
  aspects: ExtAspect[] | undefined,
  placements: PlanetsInHouses
): ExtAspect[] {
  if (!Array.isArray(aspects)) return [];
  const out: ExtAspect[] = [];
  for (const a of aspects) {
    const from = toPlanetNameLoose(a.from as unknown as string) ?? a.from;
    if (!from) continue;
    // resolve toHouse
    let toH = 0;
    if (a.toHouse) toH = clampHouse12(a.toHouse);
    else if (a.to) {
      const toPlanet = toPlanetNameLoose(a.to as unknown as string) ?? a.to;
      if (toPlanet && placements[toPlanet]) toH = clampHouse12(placements[toPlanet].house);
    }
    if (!toH) {
      const n = parseKindOrdinal(a.kind);
      const fh = (a.fromHouse && Number.isFinite(a.fromHouse)) ? clampHouse12(a.fromHouse) : clampHouse12(placements[from]?.house ?? 0);
      if (n && fh) toH = nthFrom(fh, n);
    }
    if (!toH) continue;
    out.push({ from, toHouse: toH, kind: a.kind ?? '7th' });
  }
  // dedupe
  const seen = new Set<string>();
  return out.filter(a=>{
    const k = `${a.from}|${a.toHouse}|${a.kind}`;
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
}
// ===== END INSERT: normalization + ParÄÅ›ari helpers =====

/* =============================== PROPS =============================== */
// ===== START REPLACE: Props block =====
type Props = {
  cusps: number[];                 // length 12, sidereal whole-sign cusps (deg)
  placements: PlanetsInHouses;

  size?: number;
  widthPx?: number;
  heightPx?: number;

  widthCm?: number;
  heightCm?: number;

  ascLonDeg?: number;

  retroMap?: Partial<Record<PlanetName, boolean>>;
  combustMap?: Partial<Record<PlanetName, { combust: boolean }>>;
  debilMap?: Partial<Record<PlanetName, boolean>>;
  exaltMap?: Partial<Record<PlanetName, boolean>>;
  dignityMap?: Partial<Record<PlanetName, string>>;

  // aspects source (from Calculator)
  aspects?: Array<{ from: PlanetName; to?: PlanetName; toHouse?: number; kind?: string; fromHouse?: number }>;

  getPlanetColor?: (p: PlanetName) => string;

  // layout
  scalePolicy?: 'fit' | 'no-downscale';

  // whether to render only aspecting planet NAMES (no lines)
  showAspectLabels?: boolean;  // (unused externally; we keep a local toggle)
  /** NEW: whether to include Rahu/Ketu 5/9 when auto-computing aspects */
  includeNodeSpecialAspects?: boolean; // default true
  /** Callback for SWOT analysis button */
  onAnalyseChart?: () => void;
  /** Callback for Get Remedies button */
  onGetRemedies?: () => void;
  /** House scores for visual modal */
  d1HouseScores?: Record<number, number>;
  bhavaBalaRupas?: Record<number, number>;
  houseNames?: Record<number, string>;
  onCompactViewChange?: (showCompact: boolean) => void;
  dashaContext?: any;
  defaultShowCompact?: boolean;
  /** Pre-computed per-house dasha impact (0-100, 50=neutral) */
  dashaImpactByHouse?: Record<number, number>;
  /** Pre-computed per-house transit impact (0-100, 50=neutral) */
  transitImpactByHouse?: Record<number, number>;
  /** Blended planet strength percentages (SV + Shadbala + Ishta/Kashta) */
  planetStrengthPct?: Partial<Record<string, number>>;
  /** Current transit date for the slider (shown in compact dashboard mode) */
  transitDate?: DateTime;
  /** Callback when user moves the transit date slider */
  onTransitDateChange?: (d: DateTime) => void;
  /** Planet friction scores from Karmic Pattern Detection (0-100 per planet) */
  frictionScoresByPlanet?: Record<string, number>;
  /** Opens the Karmic Pattern Detection modal */
  onKarmicPatternClick?: () => void;
  /** House interpretations for lord-in-house hover tooltips */
  houseInterpretations?: import('../utils/fetchHouseInterpretations').HouseInterpretation[];
  /** Audio guide node shown only in compact dashboard view */
  houseAudioGuide?: React.ReactNode;
  /** Called when user hovers over a planet label in the chart */
  onPlanetHover?: (planet: PlanetName | null, clientX: number, clientY: number) => void;
};
// ===== END REPLACE: Props block =====


/* ============================== helpers ============================== */
function fmtDegOnly(deg:number){
  if (!Number.isFinite(deg)) return '';
  const d = Math.floor(Math.max(0, Math.min(29.9999, deg)));
  return `${d}°`;
}
function planetShort(p: PlanetName){ return PLANET_ABBR[p] ?? p.slice(0,2); }
const BLUE_400 = '#60a5fa';
const GREEN_600 = '#16a34a';
const RED_600 = '#dc2626';
const PURPLE_600 = '#9333ea';
const BLUE_600 = '#2563eb';
const AMBER_600 = '#d97706';
const GRAY_600 = '#4b5563';
const ICON_EXALTED = '\u2191';     // ↑
const ICON_DEBILITATED = '\u2193'; // ↓
const ICON_MOOLATRIKONA = '\u2605'; // ★
const ICON_OWN = '\u25CF';         // ●
const ICON_FRIEND = '+';           // +
const ICON_ENEMY = '\u2212';       // −
const ICON_NEUTRAL = '\u25CB';     // ○
const ICON_RETRO = '\u211C';       // ℜ
const ICON_COMBUST = '\u2609';     // ☉
const ASPECT_SEPARATOR = ' \u00B7 ';

type HouseRect = {
  left:number; top:number; right:number; bottom:number; width:number; height:number; cx:number; cy:number;
};


/* ==================== GRID + LABEL OVERLAY (SVG) ==================== */
function ensureOverlay(
  host: HTMLElement,
  targetW: number,
  targetH: number,
  rasiForHouse: (h:number)=>number,   // sign number for a house (1..12)
  placements: PlanetsInHouses,
  maps: {
    retroMap?: Partial<Record<PlanetName, boolean>>;
    combustMap?: Partial<Record<PlanetName, { combust: boolean }>>;
    debilMap?: Partial<Record<PlanetName, boolean>>;
    exaltMap?: Partial<Record<PlanetName, boolean>>;
    dignityMap?: Partial<Record<PlanetName, string>>;
  },
  getPlanetColor?: (p: PlanetName) => string,
  // NOTE: these aspects can be undefined; we will auto-compute if so.
  aspects?: Array<{ from: PlanetName; to?: PlanetName; toHouse?: number; kind?: string; fromHouse?: number }>,
  houseCenters?: Array<[number,number]>,
  houseRects?: HouseRect[],
  showAspectLabels: boolean = false,
  includeNodeSpecialAspects: boolean = true,
  onPlanetHoverCb?: ((planet: PlanetName | null, clientX: number, clientY: number) => void) | null
){
  // clean previous overlay
  const old = host.querySelector('.ni-grid-overlay');
  if (old && old.parentElement) old.parentElement.removeChild(old as HTMLElement);

  const svgNS = 'http://www.w3.org/2000/svg';
  const overlaySvg = document.createElementNS(svgNS, 'svg');
  overlaySvg.classList.add('ni-grid-overlay');
  overlaySvg.setAttribute('width', String(targetW));
  overlaySvg.setAttribute('height', String(targetH));
  overlaySvg.setAttribute('viewBox', `0 0 ${targetW} ${targetH}`);
  overlaySvg.setAttribute('preserveAspectRatio', 'none');
  (overlaySvg.style as any).position = 'absolute';
  (overlaySvg.style as any).left = '0';
  (overlaySvg.style as any).top = '0';
  (overlaySvg.style as any).pointerEvents = 'none';

  const PAD = Math.round(Math.min(targetW, targetH) * 0.044);
  const X0 = PAD, Y0 = PAD;
  const W  = Math.max(40, targetW - PAD*2);
  const H  = Math.max(40, targetH - PAD*2);
  const CX = X0 + W/2, CY = Y0 + H/2;

  const TL:[number,number] = [X0, Y0];
  const TR:[number,number] = [X0+W, Y0];
  const BL:[number,number] = [X0, Y0+H];
  const BR:[number,number] = [X0+W, Y0+H];
  const TM:[number,number] = [X0+W/2, Y0];
  const BM:[number,number] = [X0+W/2, Y0+H];
  const LM:[number,number] = [X0, Y0+H/2];
  const RM:[number,number] = [X0+W, Y0+H/2];

  const STROKE = '#1e90ff';
  const SW_THICK = Math.max(1.5, Math.min(W, H) * 0.004);
  const SW = Math.max(1, Math.min(W, H) * 0.003);

  function line(x1:number,y1:number,x2:number,y2:number,w=SW){
    const el = document.createElementNS(svgNS,'line');
    el.setAttribute('x1', String(x1));
    el.setAttribute('y1', String(y1));
    el.setAttribute('x2', String(x2));
    el.setAttribute('y2', String(y2));
    el.setAttribute('stroke', STROKE);
    el.setAttribute('stroke-width', String(w));
    el.setAttribute('vector-effect','non-scaling-stroke');
    return el;
  }
  function rect(x:number,y:number,w:number,h:number,sw=SW_THICK){
    const r = document.createElementNS(svgNS,'rect');
    r.setAttribute('x', String(x)); r.setAttribute('y', String(y));
    r.setAttribute('width', String(w)); r.setAttribute('height', String(h));
    r.setAttribute('fill','none'); r.setAttribute('stroke', STROKE);
    r.setAttribute('stroke-width', String(sw));
    r.setAttribute('vector-effect','non-scaling-stroke');
    return r;
  }
  function poly(points:[number,number][], sw=SW){
    const p = document.createElementNS(svgNS,'polyline');
    p.setAttribute('points', points.map(([x,y])=>`${x},${y}`).join(' '));
    p.setAttribute('fill','none'); p.setAttribute('stroke', STROKE);
    p.setAttribute('stroke-width', String(sw));
    return p;
  }
  function textEl(
    x:number,y:number, value:string, size:number,
    weight:400|500|600|700=400, anchor:'middle'|'start'|'end'='middle', fill='#111'
  ){
    const t = document.createElementNS(svgNS,'text');
    t.setAttribute('x', String(x));
    t.setAttribute('y', String(y));
    t.setAttribute('font-size', String(size));
    t.setAttribute('font-family', "Georgia, 'Times New Roman', serif");
    t.setAttribute('text-anchor', anchor);
    t.setAttribute('fill', fill);
    (t.style as any).fontWeight = String(weight);
    t.textContent = value;
    return t;
  }
  function tspan(value:string, fill?:string, weight?:number){
    const s = document.createElementNS(svgNS,'tspan');
    if (fill) s.setAttribute('fill', fill);
    if (weight) (s.style as any).fontWeight = String(weight);
    s.textContent = value;
    return s;
  }

  const g = document.createElementNS(svgNS, 'g');

  // grid
  g.appendChild(rect(X0, Y0, W, H, SW_THICK));
  g.appendChild(line(TL[0], TL[1], BR[0], BR[1]));
  g.appendChild(line(TR[0], TR[1], BL[0], BL[1]));
  g.appendChild(poly([TM, RM, BM, LM, TM], SW));
  g.appendChild(line(TM[0], TM[1], TL[0], TL[1]));
  g.appendChild(line(TM[0], TM[1], TR[0], TR[1]));
  g.appendChild(line(BM[0], BM[1], BL[0], BL[1]));
  g.appendChild(line(BM[0], BM[1], BR[0], BR[1]));
  g.appendChild(line(LM[0], LM[1], TL[0], TL[1]));
  g.appendChild(line(LM[0], LM[1], BL[0], BL[1]));
  g.appendChild(line(RM[0], RM[1], TR[0], TR[1]));
  g.appendChild(line(RM[0], RM[1], BR[0], BR[1]));

  // HOUSE CENTERS (fallback)
  const centersFallback: Array<[number,number]> = [
    [X0+W*0.50, Y0+H*0.18],
    [X0+W*0.74, Y0+H*0.26],
    [X0+W*0.90, Y0+H*0.38],
    [X0+W*0.90, Y0+H*0.50],
    [X0+W*0.90, Y0+H*0.62],
    [X0+W*0.74, Y0+H*0.74],
    [X0+W*0.50, Y0+H*0.82],
    [X0+W*0.26, Y0+H*0.74],
    [X0+W*0.10, Y0+H*0.62],
    [X0+W*0.10, Y0+H*0.50],
    [X0+W*0.10, Y0+H*0.38],
    [X0+W*0.26, Y0+H*0.26],
  ];

  const centersByHouse: [number, number][] =
    houseCenters && houseCenters.length === 12
      ? (houseCenters as [number, number][])
      : (centersFallback as [number, number][]);

  // Fixed anchors for rashi labels
  const baseAnchors: Record<number, [number, number]> = {
     1: [X0 + W * 0.90, CY],
     2: [X0 + W * 0.75, Y0 + H * 0.20],
     3: [X0 + W * 0.50, Y0 + H * 0.16],
     4: [X0 + W * 0.25, Y0 + H * 0.20],
     5: [X0 + W * 0.10, CY - H * 0.10],
     6: [X0 + W * 0.10, CY],
     7: [X0 + W * 0.10, CY + H * 0.10],
     8: [X0 + W * 0.25, Y0 + H * 0.80],
     9: [X0 + W * 0.50, Y0 + H * 0.84],
    10: [X0 + W * 0.75, Y0 + H * 0.80],
    11: [X0 + W * 0.90, CY + H * 0.10],
    12: [X0 + W * 0.90, CY - H * 0.10],
  };

  const NUDGE_BY_HOUSE: Record<number, { dx: number; dy: number }> = {
    1: { dx:  0, dy:  95},
    2: { dx:  0, dy:  6 },
    3: { dx: 55, dy: -45 },
    4: { dx:175, dy:  6 },
    5: { dx: 55, dy: 55 },
    6: { dx:  0, dy:  5 },
    7: { dx:  0, dy: -80 },
    8: { dx:  0, dy:  10 },
    9: { dx: -50, dy: 55 },
   10: { dx: -175, dy: 40 },
   11: { dx: -55, dy:-75 },
   12: { dx: -8, dy:  0 },
  };

  const HOUSE_LABEL_OFFSET = -2;
  function anchorIndexForHouse(houseNum: number): number {
    return ((houseNum - 1 - HOUSE_LABEL_OFFSET + 12) % 12) + 1;
  }

  // rashi numbers
  const rashiFont = Math.max(11, Math.round(Math.min(W, H) * 0.036));
  for (let houseNum = 1; houseNum <= 12; houseNum++) {
    const anchorIdx = anchorIndexForHouse(houseNum);
    const [ax, ay] = baseAnchors[anchorIdx];
    const { dx, dy } = NUDGE_BY_HOUSE[houseNum] ?? { dx: 0, dy: 0 };
    const rx = ax + dx;
    const ry = ay + dy;
    g.appendChild(textEl(rx, ry, String(rasiForHouse(houseNum)), rashiFont, 600, 'middle', '#111'));
  }

  // planets grouped per house
  const byHouse: Record<number, PlanetName[]> = {};
  (Object.keys(placements) as PlanetName[]).forEach(p => {
    const h = Math.max(1, Math.min(12, Math.trunc(placements[p].house)));
    (byHouse[h] ||= []).push(p);
  });

  const planetFontBase = Math.max(11, Math.round(Math.min(W,H) * 0.035));
  const minFont = 9;
  const bleed = Math.max(6, Math.round(Math.min(W, H) * 0.012));
  const padX = bleed;
  const padTop = bleed;
  const padBottom = bleed;

  const blockByHouse: Record<number, { left:number; right:number; top:number; bottom:number; cx:number; startY:number; totalH:number; }> = {};

  function rowH(font: number){ return font + 2; }

  for (let h = 1; h <= 12; h++) {
    const rect = houseRects?.[h-1];
    const items = (byHouse[h] || []).slice();
    if (items.length && rect) {
      items.sort((a,b) => (placements[a].offsetDeg ?? 0) - (placements[b].offsetDeg ?? 0));

      const availW = Math.max(0, rect.width - padX*2);
      const availH = Math.max(0, rect.height - padTop - padBottom);

      let font = planetFontBase;
      let cols = 1;
      let rh = rowH(font);
      while (items.length * rh > availH && font > minFont) {
        font -= 1; rh = rowH(font);
      }
      if (items.length * rh > availH && availH > 0) {
        cols = Math.min(3, Math.ceil((items.length * rh) / availH));
      }
      let colW = cols > 0 ? (availW / cols) : availW;
      while (cols > 1 && colW < 24) { cols -= 1; colW = availW / cols; }
      let rowsPerCol = Math.max(1, Math.ceil(items.length / Math.max(1, cols)));
      while (rowsPerCol * rh > availH && font > minFont) {
        font -= 1; rh = rowH(font);
      }
      let neededH = rowsPerCol * rh;
      if (neededH > availH && availH > 0) {
        const scale = Math.max(0.5, Math.min(1, availH / neededH));
        const scaledFont = Math.max(7, Math.floor(font * scale));
        if (scaledFont < font) {
          font = scaledFont;
          rh = rowH(font);
          neededH = rowsPerCol * rh;
        }
      }
      while (neededH > availH && cols < 3) {
        cols += 1;
        colW = availW / cols;
        rowsPerCol = Math.max(1, Math.ceil(items.length / cols));
        neededH = rowsPerCol * rh;
      }

      const cx = rect.left + rect.width/2;
      const startY = rect.top + padTop + rh;
      const usableH = Math.max(0, Math.min(availH, rowsPerCol * rh));
      const totalH = usableH;
      blockByHouse[h] = {
        left: rect.left + padX, right: rect.right - padX,
        top: rect.top + padTop, bottom: rect.bottom - padBottom,
        cx, startY, totalH
      };

      for (let i = 0; i < items.length; i++) {
        const p = items[i];
        const col = Math.floor(i / rowsPerCol);
        const row = i % rowsPerCol;

        const colLeft = rect.left + padX + col * colW;
        const tx = colLeft + colW / 2;
        const topClamp = rect.top + padTop + rh * 0.6;
        const bottomClamp = rect.bottom - padBottom - rh * 0.4;
        const desiredY = startY + row * rh;
        const ty = Math.min(bottomClamp, Math.max(topClamp, desiredY));

      // force using the prop we pass from React; keep a clear default just in case
      const baseFill =
        (typeof getPlanetColor === 'function' ? getPlanetColor(p) : null)
        || (p === 'Rahu' || p === 'Ketu' ? '#d4b106' : '#333');


        const t = textEl(tx, ty, '', font, 700, 'middle', baseFill);
        t.setAttribute('paint-order', 'stroke');
        t.setAttribute('stroke', 'rgba(0,0,0,0.15)');
        t.setAttribute('stroke-width', '0.6');

        // Dignity symbols BEFORE planet name (if dignityMap provided, use it; otherwise fall back to exalt/debil maps)
        const dignity = maps.dignityMap?.[p];
        if (dignity) {
          // Show all 7 dignity levels
          if (dignity === 'exalted') {
            t.appendChild(tspan(`${ICON_EXALTED} `, GREEN_600, 700));
          } else if (dignity === 'debilitated') {
            t.appendChild(tspan(`${ICON_DEBILITATED} `, RED_600, 700));
          } else if (dignity === 'moolatrikona') {
            t.appendChild(tspan(`${ICON_MOOLATRIKONA} `, PURPLE_600, 700));
          } else if (dignity === 'own') {
            t.appendChild(tspan(`${ICON_OWN} `, BLUE_600, 700));
          } else if (dignity === 'friend') {
            t.appendChild(tspan(`${ICON_FRIEND} `, GREEN_600, 600));
          } else if (dignity === 'enemy') {
            t.appendChild(tspan(`${ICON_ENEMY} `, AMBER_600, 600));
          } else if (dignity === 'neutral') {
            t.appendChild(tspan(`${ICON_NEUTRAL} `, GRAY_600, 500));
          }
        } else {
          // Fallback to old exalt/debil maps for backward compatibility (D1 chart)
          if (maps.exaltMap?.[p]) t.appendChild(tspan(`${ICON_EXALTED} `, GREEN_600, 700));
          if (maps.debilMap?.[p]) t.appendChild(tspan(`${ICON_DEBILITATED} `, RED_600, 700));
        }

        t.appendChild(tspan(planetShort(p), baseFill, 700));
        
        if (maps.retroMap?.[p]) t.appendChild(tspan(` ${ICON_RETRO}`));
        if (maps.combustMap?.[p]?.combust) t.appendChild(tspan(` ${ICON_COMBUST}`));
        const od = placements[p].offsetDeg;
        if (Number.isFinite(od)) t.appendChild(tspan(` ${fmtDegOnly(od!)}`));
        // Planet hover/tap: enable pointer events on text label
        if (onPlanetHoverCb) {
          (t as SVGElement).style.pointerEvents = 'all';
          (t as SVGElement).style.cursor = 'pointer';
          const capturedP = p;
          // Desktop mouse events
          t.addEventListener('mouseenter', (e) => {
            onPlanetHoverCb(capturedP, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
          });
          t.addEventListener('mousemove', (e) => {
            onPlanetHoverCb(capturedP, (e as MouseEvent).clientX, (e as MouseEvent).clientY);
          });
          t.addEventListener('mouseleave', () => {
            onPlanetHoverCb(null, 0, 0);
          });
          // Mobile touch: tap planet label to show infolet
          t.addEventListener('touchstart', (e) => {
            e.stopPropagation(); // prevent SVG container touchstart from dismissing immediately
            const touch = (e as TouchEvent).touches[0];
            onPlanetHoverCb(capturedP, touch.clientX, touch.clientY);
          }, { passive: true });
        }
        g.appendChild(t);
      }
    }
  }

  // defs
  {
    const defs = document.createElementNS(svgNS, 'defs');
    const clip = document.createElementNS(svgNS, 'clipPath');
    clip.setAttribute('id', 'innerDiamond');
    const polyPts = document.createElementNS(svgNS, 'polygon');
    polyPts.setAttribute('points', `${TM[0]},${TM[1]} ${RM[0]},${RM[1]} ${BM[0]},${BM[1]} ${LM[0]},${LM[1]}`);
    clip.appendChild(polyPts);
    defs.appendChild(clip);
    overlaySvg.appendChild(defs);
  }

  // ---------- aspects (names only) ----------
  const ORDER: PlanetName[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

  // Normalize incoming aspects (may be Su/Mo or Surya/Shani etc.)
  const normalized = normalizeIncomingAspects(aspects as ExtAspect[] | undefined, placements);

  // If nothing passed in, auto-compute full ParÄÅ›ari from placements for parity
  const effAspects: ExtAspect[] =
    normalized.length
      ? normalized
      : computeParashariAspects(placements, includeNodeSpecialAspects);

  // Per-house unique name sets
  const namesByHouse: Record<number, Set<string>> = {};
  for (const a of effAspects) {
    const toH = a.toHouse ? clampHouse12(a.toHouse) : 0;
    const from = toPlanetNameLoose(a.from as unknown as string) ?? a.from;
    if (!toH || !from) continue;
    (namesByHouse[toH] ||= new Set()).add(planetShort(from));
  }

  if (showAspectLabels) {
    const noteFontBase = Math.max(10, Math.round(Math.min(W, H) * 0.020));
    const minNoteFont = 8;

    for (let h = 1; h <= 12; h++) {
      const set = namesByHouse[h];
      if (!set || !set.size) continue;

      const arr = Array.from(set);
      arr.sort((a, b) => {
        const pa = ORDER.findIndex(p => a === planetShort(p));
        const pb = ORDER.findIndex(p => b === planetShort(p));
        if (pa !== pb) return pa - pb;
        return a.localeCompare(b);
      });

      const rect = houseRects?.[h-1] ?? null;
      const center = centersByHouse[h-1] ?? [CX, CY];

      const availW = rect ? Math.max(0, rect.width - 8) : Math.max(0, W * 0.16);
      const baseY  = rect
        ? Math.min(rect.bottom - 4, (blockByHouse[h]?.startY ?? (rect.top + 10)) + (blockByHouse[h]?.totalH ?? 0))
        : Math.min(Y0 + H - 4, center[1] + (H * 0.06));

      let font = noteFontBase;
      const textStr = arr.join(ASPECT_SEPARATOR);
      const estWidth = (px:number) => textStr.length * (px * 0.6);
      while (estWidth(font) > availW && font > minNoteFont) font -= 1;

      const x = rect ? (rect.left + rect.width/2) : center[0];
      const y = baseY;

      const t = textEl(x, y, textStr, font, 500, 'middle', BLUE_400);
      g.appendChild(t);
    }
  }

  overlaySvg.appendChild(g);
  host.appendChild(overlaySvg);
}


/* ============================== component ============================== */
export default function NorthIndianChartVedicUMD({
  cusps, placements,
  size = 560,
  widthPx, heightPx,
  widthCm, heightCm,
  ascLonDeg, retroMap, combustMap,
  debilMap, exaltMap, dignityMap, aspects,
  getPlanetColor,
  scalePolicy = 'fit',
  includeNodeSpecialAspects = true, // NEW default
  onAnalyseChart,
  onGetRemedies,
  d1HouseScores = {},
  bhavaBalaRupas = {},
  houseNames = {},
  onCompactViewChange,
  dashaContext,
  defaultShowCompact = false,
  dashaImpactByHouse,
  transitImpactByHouse,
  planetStrengthPct,
  transitDate,
  onTransitDateChange,
  frictionScoresByPlanet,
  onKarmicPatternClick,
  houseInterpretations,
  houseAudioGuide,
  onPlanetHover,
}: Props): React.ReactElement {

  const shellRef = useRef<HTMLDivElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [renderNonce, setRenderNonce] = useState(0);
  const [showAspects, setShowAspects] = useState<boolean>(false);
  const [showCompactView, setShowCompactView] = useState(defaultShowCompact);
  // Keep a ref to the latest onPlanetHover callback so event listeners always call the current version
  const onPlanetHoverRef = useRef(onPlanetHover);
  useEffect(() => { onPlanetHoverRef.current = onPlanetHover; }, [onPlanetHover]);

  // Notify parent when compact view changes
  useEffect(() => {
    onCompactViewChange?.(showCompactView);
  }, [showCompactView, onCompactViewChange]);

  function computeHouseCenters(host: HTMLElement): Array<[number,number]> | null {
    const boxes = Array.from(host.querySelectorAll<HTMLElement>('.vc-box'));
    if (boxes.length < 12) return null;

    const hostRect = host.getBoundingClientRect();
    return boxes.slice(0, 12).map((el) => {
      const r = el.getBoundingClientRect();
      const cx = r.left - hostRect.left + r.width  / 2;
      const cy = r.top  - hostRect.top  + r.height / 2;
      return [cx, cy] as [number,number];
    });
  }

  function computeHouseRects(host: HTMLElement): HouseRect[] | null {
    const boxes = Array.from(host.querySelectorAll<HTMLElement>('.vc-box'));
    if (boxes.length < 12) return null;
    const hostRect = host.getBoundingClientRect();
    return boxes.slice(0, 12).map((el) => {
      const r = el.getBoundingClientRect();
      const left = r.left - hostRect.left;
      const top = r.top - hostRect.top;
      const width = r.width;
      const height = r.height;
      return {
        left, top, width, height,
        right: left + width,
        bottom: top + height,
        cx: left + width/2,
        cy: top + height/2
      } as HouseRect;
    });
  }

  useEffect(() => {
    if (!shellRef.current) return;
    const targetEl = shellRef.current;
    const ResizeObs = typeof window !== 'undefined' ? (window as any).ResizeObserver : undefined;
    let ro: ResizeObserver | null = null;
    if (ResizeObs) {
      let raf = 0;
      const obs = new ResizeObs(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setRenderNonce(x => x + 1));
      });
      ro = obs;
      try { obs.observe?.(targetEl); } catch {}
    }
    return () => { try { ro?.disconnect?.(); } catch {} };
  }, []);

  useEffect(() => {
    // Don't render chart when showing compact view
    if (showCompactView) {
      // Clear any existing chart when switching to compact view
      if (shellRef.current) {
        shellRef.current.innerHTML = '';
      }
      return;
    }
    if (!shellRef.current) return;

    const host = shellRef.current;

    // resolve WÃ-H
    const cmWpx = typeof widthCm === 'number' && widthCm > 0 ? cmToPx(widthCm) : undefined;
    const cmHpx = typeof heightCm === 'number' && heightCm > 0 ? cmToPx(heightCm) : undefined;

    const box = host.getBoundingClientRect();
    const containerW = Math.floor(box?.width  || size);
    const containerH = Math.floor(box?.height || size);

    const resolvedW = cmWpx ?? widthPx ?? containerW ?? size;
    const resolvedH = cmHpx ?? heightPx ?? containerH ?? size;

    const targetW = Math.max(200, Math.min(Math.round(resolvedW), 4096));
    const targetH = Math.max(200, Math.min(Math.round(resolvedH), 4096));

    // style host
    host.style.width = `${targetW}px`;
    host.style.height = `${targetH}px`;
    host.style.position = 'relative';
    host.style.overflow = 'visible';
    host.style.maxWidth = '100%';
    host.style.display = 'block';
    host.style.backgroundColor = '#fff';
    host.style.borderRadius = '12px';
    host.style.boxShadow = '0 4px 18px rgba(0,0,0,0.08)';

    // vendor API
    const API =
      (window as any).NorthIndianChart ||
      (window as any).VedicChart?.NorthIndianChart ||
      (window as any).VedicChart;

    if (!API) {
      setErr('NorthIndianChart global not found. Check index.html script include.');
      return;
    }
    setErr(null);

    // Signs from CUSPS
    const cuspSigns = (Array.isArray(cusps) && cusps.length >= 12)
      ? cusps.slice(0,12).map(signNum1FromLon)
      : Array.from({length:12},()=>1);

    const ascSign = cuspSigns[0]; // 1..12
    const ascDegInSign = Math.max(0, Math.min(29.9999, norm360(ascLonDeg ?? 0) % 30));

    const rasiForHouse = (h:number) => ((ascSign - 1 + (h - 1) + 1200) % 12) + 1;

    try {
      (window as any).__UMD = {
        cuspSigns: [...cuspSigns],
        ascSign,
        rasiSeq: Array.from({ length: 12 }, (_, i) => rasiForHouse(i + 1)),
      };
    } catch {}

    const houseRasi = (h:number) => signNum1FromLon(cusps?.[h-1] ?? 0);
    const vendorPlanets = (Object.keys(placements) as PlanetName[]).map(p => {
      const { house, offsetDeg } = placements[p];
      const retro = !!retroMap?.[p];
      const hasDeg = Number.isFinite(offsetDeg) && Math.abs(offsetDeg) > 1e-3;
      const d      = hasDeg ? Math.max(0, Math.min(29.9999, offsetDeg)) : 0;
      return { name: PLANET_ABBR[p] + (retro ? ` ${ICON_RETRO}` : ''), rasi_no: houseRasi(house), house, retro, combust:false, degree:d };
    });

    // render vendor base
    host.innerHTML = '';
    try {
      API(host, {
        ascendant: { rasi_no: ascSign, degree: ascDegInSign },
        planets: vendorPlanets,
        width: targetW, height: targetH
      });
    } catch (e:any) {
      try {
        const html = API([
          { name: 'Lagna', rasi_no: ascSign, house: 1, retro: false, combust: false, degree: ascDegInSign },
          ...vendorPlanets
        ]);
        if (typeof html === 'string') host.innerHTML = html;
      } catch (e2:any) {
        setErr(`Chart init failed: ${e2?.message || e2}`);
      }
    }

    const centers = computeHouseCenters(host);
    const rects = computeHouseRects(host);

// hide ONLY vendor text; keep our SVG overlay visible
const hideCSS = document.createElement('style');
hideCSS.textContent = `
  .vc-box, .vc-rashi-layer { visibility: hidden !important; }
`;
host.appendChild(hideCSS);

// ensure our overlay text is visible even if other CSS leaks in
const showCSS = document.createElement('style');
showCSS.textContent = `
  .ni-grid-overlay, .ni-grid-overlay text { visibility: visible !important; }
`;
host.appendChild(showCSS);


// draw overlay
    ensureOverlay(
      host,
      targetW,
      targetH,
      rasiForHouse,
      placements,
      { retroMap, combustMap, debilMap, exaltMap, dignityMap },
      getPlanetColor,
      aspects as ExtAspect[] | undefined,
      centers || undefined,
      rects || undefined,
      showAspects,                 // names toggle
      includeNodeSpecialAspects,   // NEW: include Rahu/Ketu special aspects when auto-computing
      onPlanetHoverRef.current,    // hover callback (always reads latest via ref)
    );

  }, [
    cusps, placements, size, widthPx, heightPx, widthCm, heightCm,
    ascLonDeg, retroMap, combustMap, debilMap, exaltMap, dignityMap, aspects, getPlanetColor, scalePolicy, renderNonce,
    showAspects, includeNodeSpecialAspects, showCompactView, onPlanetHover
  ]);

  return (
    <div style={{ 
      width:'100%', 
      display:'flex', 
      flexDirection:'column', 
      gap:8
    }}>
      {/* Toggle row */}
      <div style={{ 
        display:'flex', 
        alignItems:'center', 
        gap: 8, 
        fontFamily:'ui-sans-serif, system-ui', 
        fontSize: 13,
        flexWrap: 'wrap'
      }}>
        <label 
          style={{ display: showCompactView ? 'none' : 'inline-flex', alignItems:'center', gap:6, cursor:'pointer', userSelect:'none', opacity: showCompactView ? 0.5 : 1 }}
          title="View Planet Drishti"
        >
          <input
            type="checkbox"
            checked={showAspects}
            onChange={(e) => setShowAspects(e.target.checked)}
            disabled={showCompactView}
            style={{ cursor: showCompactView ? 'not-allowed' : 'pointer' }}
          />
          <span style={{ color:'#334155', fontSize:12 }}>Show Aspects</span>
        </label>
        {showCompactView && houseAudioGuide && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {houseAudioGuide}
          </div>
        )}
        {dignityMap && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 11 }}>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>⬆ Exalted</span>
            <span style={{ color: '#9333ea', fontWeight: 600 }}>★ Moolatrikona</span>
            <span style={{ color: '#2563eb', fontWeight: 600 }}>● Own</span>
            <span style={{ color: '#16a34a', fontWeight: 500, opacity: 0.7 }}>+ Friend</span>
            <span style={{ color: '#4b5563', fontWeight: 500 }}>○ Neutral</span>
            <span style={{ color: '#d97706', fontWeight: 600 }}>− Enemy</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>⬇ Debilitated</span>
          </div>
        )}
        {Object.keys(d1HouseScores).length > 0 && (
          <button
            data-audio-cue="home-show-chart-btn"
            onClick={() => setShowCompactView(!showCompactView)}
            className="gradient-border-button"
            style={{
              flexShrink: 0,
              backgroundColor: showCompactView ? 'rgba(33, 150, 243, 0.1)' : 'transparent'
            }}
          >
            <div className="gradient-border-button-inner">
              <span style={{ fontSize: '14px', lineHeight: 1 }}>{showCompactView ? '🔄' : '📊'}</span>
              <span>{showCompactView ? 'Show Chart' : 'Dashboard'}</span>
            </div>
          </button>
        )}
        {onAnalyseChart && (
          <button
            data-audio-cue="home-analyse-chart-btn"
            onClick={onAnalyseChart}
            className="gradient-border-button"
            style={{
              flexShrink: 0
            }}
          >
            <div className="gradient-border-button-inner">
              <span style={{ fontSize: '14px', lineHeight: 1 }}>🔮</span>
              <span>Analyse Chart</span>
            </div>
          </button>
        )}
        {onGetRemedies && (
          <button
            data-audio-cue="home-get-remedies-btn"
            onClick={onGetRemedies}
            className="gradient-border-button"
            style={{
              flexShrink: 0
            }}
          >
            <div className="gradient-border-button-inner">
              <span style={{ fontSize: '14px', lineHeight: 1 }}>🌿</span>
              <span>Get Remedies</span>
            </div>
          </button>
        )}
      </div>

      {/* Transit date slider + Karmic Pattern button — same row, compact dashboard mode only */}
      {showCompactView && (transitDate || onKarmicPatternClick) && (
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, width: '100%', marginTop: 6, marginBottom: 2 }}>
          {transitDate && onTransitDateChange && (
            <div style={{ width: 340, flexShrink: 0 }}>
              <TransitDateSlider value={transitDate} onChange={onTransitDateChange} />
            </div>
          )}
          {onKarmicPatternClick && (
            <button
              data-audio-cue="home-karmic-friction-btn"
              onClick={onKarmicPatternClick}
              style={{
                marginLeft: 'auto',
                flexShrink: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '0px 14px',
                alignSelf: 'stretch',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              <span style={{ fontSize: '14px', lineHeight: 1 }}>🔮</span>
              <span>Get Karmic Friction Index</span>
            </button>
          )}
        </div>
      )}

      {/* Chart shell or Compact View */}
      <div style={{ 
        width:'100%', 
        display:'flex', 
        gap:12, 
        alignItems:'flex-start'
      }}>
        <div
          ref={shellRef}
          style={{
            background:'#fff',
            borderRadius:12,
            boxShadow:'0 4px 18px rgba(0,0,0,0.08)',
            overflow:'visible',
            position:'relative',
            width:'100%',
            display: showCompactView ? 'none' : 'block'
          }}
        />
        {showCompactView && (
          <div style={{ 
            width: '100%', 
            display: 'flex', 
            justifyContent: 'center'
          }}>
            <div style={{ width: '100%', maxWidth: '1200px' }}>
              <HouseScoresCompactView
                d1HouseScores={d1HouseScores}
                bhavaBalaRupas={bhavaBalaRupas}
                houseNames={houseNames}
                dashaContext={dashaContext}
                ascendantSignIndex={ascLonDeg ? signNum1FromLon(ascLonDeg) : undefined}
                dashaImpactByHouse={dashaImpactByHouse}
                transitImpactByHouse={transitImpactByHouse}
                planetStrengthPct={planetStrengthPct}
                frictionScoresByPlanet={frictionScoresByPlanet}
                onKarmicPatternClick={undefined}
                houseInterpretations={houseInterpretations}
                planetPlacements={placements ? Object.fromEntries(
                  Object.entries(placements).map(([planet, data]: [string, any]) => {
                    const pName = planet as PlanetName;
                    let dignity: 'exalted' | 'debilitated' | undefined;
                    if (exaltMap && exaltMap[pName]) {
                      dignity = 'exalted';
                    } else if (debilMap && debilMap[pName]) {
                      dignity = 'debilitated';
                    }
                    return [planet, { house: data.house, dignity }];
                  })
                ) : {}}
              />
            </div>
          </div>
        )}
        {err && (
          <div style={{ color:'#b91c1c', fontFamily:'ui-sans-serif, system-ui', fontSize:14 }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
// ===== END FILE: src/components/NorthIndianChartVedicUMD.tsx =====


